import { useContext, useMemo, type ReactNode } from "react";
import { usePermissions } from "../../../../session/permissions";
import { useAssignableMembers, useTeams } from "../../../build/shared/queries";
import { useAttributes, useSlaPlan, useSlaPolicies } from "../../../workspace/shared";
import { useAgentList } from "../../shared/use-engage-data";
import { useWorkflowActions } from "../use-workflow-actions";
import { useArticleSources } from "../use-article-sources";
import { useEndpoints, useLeadStages, useWorkflowList } from "../use-workflows";
import { RefsContext, type BuilderRefs, type Option } from "./refs-context";
import { useLeadOwners } from "./system/lead-owners";
import { ServiceDeskContext } from "../service/service-desk-context";
import { useServiceDesk } from "../service/use-service-desk";
import { useServiceRefs } from "../service/use-service-refs";

export type { BuilderRefs, Option, ReusableOption } from "./refs-context";

export function useRefs(): BuilderRefs {
  return useContext(RefsContext);
}

export function nameOf(options: readonly Option[], id: string | undefined, fallback: string): string {
  return options.find((option) => option.id === id)?.name ?? fallback;
}

/** Loads the reference lists the member may read; a list they can't read stays empty. `selfId` is left out of the reusable workflows (a workflow can't run itself). */
export function RefsProvider({ children, selfId }: { children: ReactNode; selfId?: string }) {
  const permissions = usePermissions();
  const endpoints = useEndpoints(permissions.can("webhooks.manage"));
  const teams = useTeams(permissions.can("team.view"));
  const members = useAssignableMembers(permissions.can("team.view"));
  const leadOwners = useLeadOwners(permissions.can("team.view"), members.data);
  const stages = useLeadStages(permissions.can("leads.view"));
  const agents = useAgentList();
  const { actions, loading: actionsLoading } = useWorkflowActions(permissions.can("actions.view"));
  // The attribute and SLA policy lists are readable with workflows.view (editing them is settings.manage).
  const canReadWorkflows = permissions.can("workflows.view");
  const attributes = useAttributes(canReadWorkflows);
  const slaPlan = useSlaPlan();
  const slaPolicies = useSlaPolicies(canReadWorkflows && slaPlan.ready && !slaPlan.blocked);
  const workflows = useWorkflowList(canReadWorkflows);
  const sources = useArticleSources("", permissions.can("knowledge.view"));
  // The Service Desk lists are read only where the Service Desk can be used (the server's navigation says so) and the member may view tickets.
  const { state: serviceDeskState, minPlan: serviceMinPlan } = useServiceDesk();
  const serviceDesk = useMemo(() => ({ state: serviceDeskState, minPlan: serviceMinPlan }), [serviceDeskState, serviceMinPlan]);
  const service = useServiceRefs(serviceDeskState === "available");
  const value = useMemo<BuilderRefs>(
    () => ({
      endpoints: (endpoints.data ?? []).map((e) => ({ id: e.id, name: e.description ?? e.url })),
      teams: (teams.data ?? []).map((t) => ({ id: t.id, name: t.name })),
      members: (members.data ?? []).map((m) => ({ id: m.user_id, name: m.name })),
      leadOwners,
      stages: (stages.data ?? []).map((s) => ({ id: s.id, name: s.name })),
      agents: (agents.data ?? []).map((a) => ({ id: a.id, name: a.name })),
      actions,
      attributes: attributes.data ?? [],
      slaPolicies: (slaPolicies.data ?? []).map((p) => ({ id: p.id, name: p.name })),
      sources: (sources.data ?? []).map((s) => ({ id: s.id, name: s.title })),
      service: serviceDeskState === "available" ? service : undefined,
      loading: endpoints.isLoading || teams.isLoading || members.isLoading || stages.isLoading || agents.isLoading || actionsLoading,
      reusables: (workflows.data ?? []).filter((w) => w.trigger === "reusable" && w.kind === "background" && w.id !== selfId).map((w) => ({ id: w.id, name: w.name, state: w.state })),
    }),
    [endpoints.data, endpoints.isLoading, teams.data, teams.isLoading, members.data, members.isLoading, leadOwners, stages.data, stages.isLoading, agents.data, agents.isLoading, sources.data, actions, actionsLoading, attributes.data, slaPolicies.data, workflows.data, selfId, service, serviceDeskState],
  );
  return (
    <ServiceDeskContext.Provider value={serviceDesk}>
      <RefsContext.Provider value={value}>{children}</RefsContext.Provider>
    </ServiceDeskContext.Provider>
  );
}
